/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  StopCircleIcon,
  ClockIcon,
  ChartBarIcon,
  TableCellsIcon,
  TrashIcon,
  BeakerIcon,
  TagIcon,
  CubeIcon,
  CircleStackIcon,
  ArchiveBoxIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
  BellAlertIcon,
  DocumentTextIcon,
  PlusIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import {
  runService,
  datasetService,
  artifactService,
  alertService,
  runNotesService,
  RunDetail as RunDetailType,
  Metric,
  Dataset,
  ArtifactInfo,
  RunAlert,
  normalizeDataset,
} from '../../services/registryApi';
import toast from 'react-hot-toast';

const CHART_COLORS = [
  '#F97316', // orange (primary)
  '#10B981', // green
  '#3B82F6', // blue
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F59E0B', // amber
];

const RunStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
    running: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: PlayIcon },
    completed: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircleIcon },
    failed: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircleIcon },
    killed: { bg: 'bg-gray-700', text: 'text-gray-300', icon: StopCircleIcon },
  };

  const { bg, text, icon: Icon } = config[status] || config.running;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${bg} ${text}`}>
      <Icon className="w-4 h-4" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const formatDuration = (ms: number): string => {
  if (!ms) return '-';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
};

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
}> = ({ title, value, icon: Icon, color }) => (
  <div className="bg-gray-900 rounded-xl border border-gray-700 p-4">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-xs text-gray-400">{title}</p>
        <p className="text-lg font-semibold text-white">{value}</p>
      </div>
    </div>
  </div>
);

const RunDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [runDetail, setRunDetail] = useState<RunDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [metricsHistory, setMetricsHistory] = useState<Record<string, Metric[]>>({});
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'metrics' | 'params' | 'tags' | 'datasets' | 'artifacts' | 'notes' | 'alerts'>('metrics');
  const [deleting, setDeleting] = useState(false);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [datasetsLoading, setDatasetsLoading] = useState(false);
  const [expandedSchema, setExpandedSchema] = useState<string | null>(null);
  const [artifacts, setArtifacts] = useState<ArtifactInfo[]>([]);
  const [artifactsLoading, setArtifactsLoading] = useState(false);
  const [downloadingArtifact, setDownloadingArtifact] = useState<string | null>(null);
  // Notes state
  const [notes, setNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [expandedEnvParams, setExpandedEnvParams] = useState(false);
  const [expandedSysParams, setExpandedSysParams] = useState(false);
  // Alerts state
  const [alerts, setAlerts] = useState<RunAlert[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [showCreateAlert, setShowCreateAlert] = useState(false);
  const [newAlert, setNewAlert] = useState({ metric_key: '', condition: 'lt', threshold: '', notify_email: '', notify_slack: '' });
  const [creatingAlert, setCreatingAlert] = useState(false);

  useEffect(() => {
    const fetchRunDetail = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const response = await runService.getDetail(id);
        setRunDetail(response.data);
        // Auto-select first metric if available
        if (response.data.metric_keys?.length > 0) {
          setSelectedMetrics([response.data.metric_keys[0]]);
        }
      } catch (error) {
        console.error('Failed to fetch run detail:', error);
        toast.error('Failed to load run details');
      } finally {
        setLoading(false);
      }
    };

    fetchRunDetail();
  }, [id]);

  // Fetch metric history for selected metrics
  useEffect(() => {
    const fetchMetricHistory = async () => {
      if (!id || selectedMetrics.length === 0) {
        setMetricsHistory({});
        return;
      }

      const history: Record<string, Metric[]> = {};
      for (const metricKey of selectedMetrics) {
        try {
          const response = await runService.getMetricHistory(id, metricKey);
          history[metricKey] = response.data.metrics || [];
        } catch (error) {
          console.error(`Failed to fetch metric history for ${metricKey}:`, error);
        }
      }
      setMetricsHistory(history);
    };

    fetchMetricHistory();
  }, [id, selectedMetrics]);

  // Fetch datasets when tab is activated
  useEffect(() => {
    if (activeTab !== 'datasets' || !id) return;
    const fetchDatasets = async () => {
      try {
        setDatasetsLoading(true);
        const response = await datasetService.getRunDatasets(id);
        setDatasets((response.data.datasets || []).map(normalizeDataset));
      } catch {
        // silently ignore - empty state handled in render
      } finally {
        setDatasetsLoading(false);
      }
    };
    fetchDatasets();
  }, [activeTab, id]);

  // Initialize notes from run data
  useEffect(() => {
    if (runDetail?.run) {
      setNotes((runDetail.run as any).notes || '');
    }
  }, [runDetail]);

  // Fetch alerts when tab is activated
  useEffect(() => {
    if (activeTab !== 'alerts' || !id) return;
    const fetchAlerts = async () => {
      try {
        setAlertsLoading(true);
        const response = await alertService.list(id);
        setAlerts(response.data.alerts || []);
      } catch {
        setAlerts([]);
      } finally {
        setAlertsLoading(false);
      }
    };
    fetchAlerts();
  }, [activeTab, id]);

  // Fetch artifacts when tab is activated
  useEffect(() => {
    if (activeTab !== 'artifacts' || !id) return;
    const fetchArtifacts = async () => {
      try {
        setArtifactsLoading(true);
        const response = await artifactService.listArtifacts(id);
        setArtifacts(response.data.artifacts || []);
      } catch {
        setArtifacts([]);
      } finally {
        setArtifactsLoading(false);
      }
    };
    fetchArtifacts();
  }, [activeTab, id]);

  const handleSaveNotes = async () => {
    if (!id) return;
    setNotesSaving(true);
    try {
      await runNotesService.update(id, notes);
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setNotesSaving(false);
    }
  };

  const handleCreateAlert = async () => {
    if (!id || !newAlert.metric_key || !newAlert.condition || newAlert.threshold === '') return;
    setCreatingAlert(true);
    try {
      const response = await alertService.create(id, {
        metric_key: newAlert.metric_key,
        condition: newAlert.condition,
        threshold: parseFloat(newAlert.threshold),
        notify_email: newAlert.notify_email || undefined,
        notify_slack: newAlert.notify_slack || undefined,
      });
      setAlerts((prev) => [response.data.alert, ...prev]);
      setShowCreateAlert(false);
      setNewAlert({ metric_key: '', condition: 'lt', threshold: '', notify_email: '', notify_slack: '' });
      toast.success('Alert created');
    } catch {
      toast.error('Failed to create alert');
    } finally {
      setCreatingAlert(false);
    }
  };

  const handleDeleteAlert = async (alertId: string) => {
    try {
      await alertService.delete(alertId);
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      toast.success('Alert deleted');
    } catch {
      toast.error('Failed to delete alert');
    }
  };

  const handleDownloadArtifact = async (artifactPath: string) => {
    if (!id) return;
    setDownloadingArtifact(artifactPath);
    try {
      const response = await artifactService.getDownloadUrl(id, artifactPath);
      const url = response.data.download_url;
      const a = document.createElement('a');
      a.href = url;
      a.download = artifactPath.split('/').pop() || 'artifact';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      toast.error('Failed to get download link');
    } finally {
      setDownloadingArtifact(null);
    }
  };

  const handleMetricToggle = (metricKey: string) => {
    setSelectedMetrics((prev) =>
      prev.includes(metricKey) ? prev.filter((k) => k !== metricKey) : [...prev, metricKey]
    );
  };

  const handleDeleteRun = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this run? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      await runService.delete(id);
      toast.success('Run deleted successfully');
      if (runDetail?.experiment) {
        navigate(`/registry/experiments/${runDetail.experiment.id}`);
      } else {
        navigate('/registry/experiments');
      }
    } catch (error) {
      console.error('Failed to delete run:', error);
      toast.error('Failed to delete run');
    } finally {
      setDeleting(false);
    }
  };

  // Prepare chart data
  const chartData = useMemo(() => {
    if (selectedMetrics.length === 0) return [];

    // Collect all steps across selected metrics
    const stepSet = new Set<number>();
    selectedMetrics.forEach((key) => {
      metricsHistory[key]?.forEach((m) => stepSet.add(m.step));
    });

    const steps = Array.from(stepSet).sort((a, b) => a - b);

    return steps.map((step) => {
      const point: Record<string, number> = { step };
      selectedMetrics.forEach((key) => {
        const metric = metricsHistory[key]?.find((m) => m.step === step);
        if (metric) {
          point[key] = metric.value;
        }
      });
      return point;
    });
  }, [selectedMetrics, metricsHistory]);

  // Prepare all metrics for the table
  const metricsTableData = useMemo(() => {
    if (!runDetail) return [];
    // Group metrics by key
    const grouped: Record<string, Metric[]> = {};
    runDetail.metrics.forEach((m) => {
      if (!grouped[m.key]) {
        grouped[m.key] = [];
      }
      grouped[m.key].push(m);
    });
    return Object.entries(grouped).map(([key, values]) => ({
      key,
      values: values.sort((a, b) => a.step - b.step),
      latest: values[values.length - 1]?.value,
      min: Math.min(...values.map((v) => v.value)),
      max: Math.max(...values.map((v) => v.value)),
      count: values.length,
    }));
  }, [runDetail]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-gray-700 border-t-primary-500"></div>
      </div>
    );
  }

  if (!runDetail) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <PlayIcon className="mx-auto h-12 w-12 text-gray-600" />
          <h3 className="mt-2 text-sm font-medium text-white">Run not found</h3>
          <button
            onClick={() => navigate('/registry/experiments')}
            className="mt-4 text-primary-500 hover:text-primary-400"
          >
            Back to experiments
          </button>
        </div>
      </div>
    );
  }

  const { run, experiment, params, metric_keys, latest_metrics } = runDetail;

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => experiment ? navigate(`/registry/experiments/${experiment.id}`) : navigate('/registry/experiments')}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to {experiment?.name || 'Experiments'}
          </button>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <PlayIcon className="w-8 h-8 text-primary-500" />
                <h1 className="text-2xl font-bold text-white">
                  {run.name || `Run ${run.id.slice(0, 8)}`}
                </h1>
                <RunStatusBadge status={run.status} />
              </div>
              {experiment && (
                <div className="flex items-center gap-2 mt-2 text-gray-400">
                  <BeakerIcon className="w-4 h-4" />
                  <span>Experiment: {experiment.name}</span>
                </div>
              )}
            </div>
            <button
              onClick={handleDeleteRun}
              disabled={deleting}
              className="flex items-center gap-2 px-4 py-2 text-red-500 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50"
            >
              {deleting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
              ) : (
                <TrashIcon className="w-4 h-4" />
              )}
              Delete Run
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Duration"
            value={formatDuration(run.duration_ms)}
            icon={ClockIcon}
            color="bg-primary-500"
          />
          <StatCard
            title="Metrics"
            value={metric_keys?.length || 0}
            icon={ChartBarIcon}
            color="bg-green-500"
          />
          <StatCard
            title="Parameters"
            value={params?.length || 0}
            icon={CubeIcon}
            color="bg-purple-500"
          />
          <StatCard
            title="Tags"
            value={Object.keys(run.tags || {}).length}
            icon={TagIcon}
            color="bg-amber-500"
          />
        </div>

        {/* Run Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 bg-gray-900 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Run Information</h3>
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-gray-400">Run ID</dt>
                <dd className="text-sm font-mono text-white mt-1">{run.id}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-400">Status</dt>
                <dd className="mt-1"><RunStatusBadge status={run.status} /></dd>
              </div>
              <div>
                <dt className="text-sm text-gray-400">Start Time</dt>
                <dd className="text-sm text-white mt-1">
                  {run.start_time ? new Date(run.start_time * 1000).toLocaleString() : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-400">End Time</dt>
                <dd className="text-sm text-white mt-1">
                  {run.end_time ? new Date(run.end_time * 1000).toLocaleString() : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-400">Source Type</dt>
                <dd className="text-sm text-white mt-1">{run.source_type || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-400">Source Name</dt>
                <dd className="text-sm text-white mt-1 truncate">{run.source_name || '-'}</dd>
              </div>
            </dl>
          </div>

          {/* Latest Metrics Card */}
          <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Latest Metrics</h3>
            {Object.keys(latest_metrics || {}).length === 0 ? (
              <p className="text-sm text-gray-400">No metrics logged</p>
            ) : (
              <dl className="space-y-3 max-h-48 overflow-y-auto">
                {Object.entries(latest_metrics || {}).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <dt className="text-sm text-gray-400 truncate flex-1">{key}</dt>
                    <dd className="text-sm font-mono font-medium text-white ml-2">
                      {typeof value === 'number' ? value.toFixed(4) : value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-700 mb-6">
          <nav className="flex gap-8">
            <button
              onClick={() => setActiveTab('metrics')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'metrics'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <ChartBarIcon className="w-4 h-4 inline mr-2" />
              Metrics
            </button>
            <button
              onClick={() => setActiveTab('params')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'params'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <CubeIcon className="w-4 h-4 inline mr-2" />
              Parameters
            </button>
            <button
              onClick={() => setActiveTab('tags')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'tags'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <TagIcon className="w-4 h-4 inline mr-2" />
              Tags
            </button>
            <button
              onClick={() => setActiveTab('datasets')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'datasets'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <CircleStackIcon className="w-4 h-4 inline mr-2" />
              Datasets
            </button>
            <button
              onClick={() => setActiveTab('artifacts')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'artifacts'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <ArchiveBoxIcon className="w-4 h-4 inline mr-2" />
              Artifacts
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'notes'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <DocumentTextIcon className="w-4 h-4 inline mr-2" />
              Notes
            </button>
            <button
              onClick={() => setActiveTab('alerts')}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === 'alerts'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <BellAlertIcon className="w-4 h-4 inline mr-2" />
              Alerts
            </button>
          </nav>
        </div>

        {/* Metrics Tab */}
        {activeTab === 'metrics' && (
          <div className="space-y-6">
            {/* Metric Selector */}
            {metric_keys && metric_keys.length > 0 && (
              <div className="bg-gray-900 rounded-xl border border-gray-700 p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">Select metrics to visualize:</h4>
                <div className="flex flex-wrap gap-2">
                  {metric_keys.map((key, index) => (
                    <button
                      key={key}
                      onClick={() => handleMetricToggle(key)}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        selectedMetrics.includes(key)
                          ? 'text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                      style={
                        selectedMetrics.includes(key)
                          ? { backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }
                          : undefined
                      }
                    >
                      {key}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Metrics Chart */}
            {selectedMetrics.length > 0 && chartData.length > 0 && (
              <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Metrics Over Time</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3d" />
                      <XAxis
                        dataKey="step"
                        stroke="#9CA3AF"
                        tick={{ fill: '#9CA3AF' }}
                        label={{ value: 'Step', position: 'insideBottom', offset: -5, fill: '#9CA3AF' }}
                      />
                      <YAxis
                        stroke="#9CA3AF"
                        tick={{ fill: '#9CA3AF' }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1a1a28',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                        }}
                      />
                      <Legend />
                      {selectedMetrics.map((key, index) => (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          stroke={CHART_COLORS[metric_keys?.indexOf(key) ?? index % CHART_COLORS.length]}
                          strokeWidth={2}
                          dot={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Metrics Table */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden">
              <h3 className="text-lg font-semibold text-white p-6 pb-4">All Metrics</h3>
              {metricsTableData.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  No metrics logged for this run
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                          Metric
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                          Latest Value
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                          Min
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                          Max
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                          Steps
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {metricsTableData.map((metric) => (
                        <tr key={metric.key} className="hover:bg-gray-800">
                          <td className="px-6 py-4 text-sm font-medium text-white">
                            {metric.key}
                          </td>
                          <td className="px-6 py-4 text-sm font-mono text-gray-300">
                            {metric.latest?.toFixed(6)}
                          </td>
                          <td className="px-6 py-4 text-sm font-mono text-gray-300">
                            {metric.min?.toFixed(6)}
                          </td>
                          <td className="px-6 py-4 text-sm font-mono text-gray-300">
                            {metric.max?.toFixed(6)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-300">
                            {metric.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Parameters Tab */}
        {activeTab === 'params' && (() => {
          const envParams = params?.filter((p) => p.key.startsWith('_env.')) || [];
          const sysParams = params?.filter((p) => p.key.startsWith('_sys.')) || [];
          const privateParams = params?.filter((p) =>
            p.key.startsWith('_tag.') || p.key.startsWith('_model.') ||
            p.key.startsWith('_artifact.') || p.key.startsWith('_run.') ||
            p.key === '_artifact_store'
          ) || [];
          const userParams = params?.filter((p) =>
            !p.key.startsWith('_env.') && !p.key.startsWith('_sys.') &&
            !p.key.startsWith('_tag.') && !p.key.startsWith('_model.') &&
            !p.key.startsWith('_artifact.') && !p.key.startsWith('_run.') &&
            p.key !== '_artifact_store'
          ) || [];

          const ParamTable = ({ rows }: { rows: typeof params }) => (
            <table className="min-w-full">
              <tbody className="divide-y divide-gray-700">
                {(rows || []).map((p) => (
                  <tr key={p.id} className="hover:bg-gray-800">
                    <td className="px-6 py-3 text-sm font-medium text-white w-1/2">{p.key}</td>
                    <td className="px-6 py-3 text-sm font-mono text-gray-300">{p.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          );

          return (
            <div className="space-y-4">
              {/* User params */}
              <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden">
                <h3 className="text-lg font-semibold text-white p-6 pb-4">Parameters</h3>
                {userParams.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">No hyperparameters logged</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-800">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Parameter</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Value</th>
                        </tr>
                      </thead>
                      <ParamTable rows={userParams} />
                    </table>
                  </div>
                )}
              </div>

              {/* Environment group */}
              {envParams.length > 0 && (
                <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-800 transition-colors"
                    onClick={() => setExpandedEnvParams((v) => !v)}
                  >
                    <div className="flex items-center gap-2">
                      <ComputerDesktopIcon className="w-5 h-5 text-green-400" />
                      <span className="text-sm font-semibold text-white">Environment Capture</span>
                      <span className="text-xs text-gray-400">({envParams.length} captured)</span>
                    </div>
                    <span className="text-gray-400 text-sm">{expandedEnvParams ? '▾' : '▸'}</span>
                  </button>
                  {expandedEnvParams && (
                    <div className="overflow-x-auto border-t border-gray-700">
                      <table className="min-w-full">
                        <ParamTable rows={envParams.map((p) => ({ ...p, key: p.key.replace('_env.', '') }))} />
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* System metrics group */}
              {sysParams.length > 0 && (
                <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-800 transition-colors"
                    onClick={() => setExpandedSysParams((v) => !v)}
                  >
                    <div className="flex items-center gap-2">
                      <ChartBarIcon className="w-5 h-5 text-blue-400" />
                      <span className="text-sm font-semibold text-white">System Metrics (latest)</span>
                      <span className="text-xs text-gray-400">({sysParams.length} metrics)</span>
                    </div>
                    <span className="text-gray-400 text-sm">{expandedSysParams ? '▾' : '▸'}</span>
                  </button>
                  {expandedSysParams && (
                    <div className="overflow-x-auto border-t border-gray-700">
                      <table className="min-w-full">
                        <ParamTable rows={sysParams.map((p) => ({ ...p, key: p.key.replace('_sys.', '') }))} />
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Tags Tab */}
        {activeTab === 'tags' && (
          <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Tags</h3>
            {!run.tags || Object.keys(run.tags).length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                No tags set for this run
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(run.tags).map(([key, value]) => (
                  <div
                    key={key}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-700 rounded-lg"
                  >
                    <span className="text-sm font-medium text-gray-300">{key}:</span>
                    <span className="text-sm text-gray-400">{value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Artifacts Tab */}
        {activeTab === 'artifacts' && (
          <div>
            {artifactsLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              </div>
            ) : artifacts.length === 0 ? (
              <div className="bg-gray-900 rounded-xl border border-gray-700 p-12 text-center">
                <ArchiveBoxIcon className="mx-auto h-12 w-12 text-gray-600 mb-3" />
                <h3 className="text-sm font-medium text-white mb-1">No artifacts stored</h3>
                <p className="text-sm text-gray-400">
                  Call{' '}
                  <code className="font-mono bg-gray-800 px-1 rounded">run.log_artifact()</code>{' '}
                  or{' '}
                  <code className="font-mono bg-gray-800 px-1 rounded">run.log_model()</code>{' '}
                  in your training code to store files.
                </p>
              </div>
            ) : (
              <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Artifacts</h3>
                  <span className="text-sm text-gray-400">{artifacts.length} file{artifacts.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="divide-y divide-gray-700">
                  {artifacts.map((artifact) => {
                    const ext = artifact.path.split('.').pop()?.toLowerCase() || '';
                    const extColors: Record<string, string> = {
                      pt: 'bg-orange-500/20 text-orange-400',
                      pth: 'bg-orange-500/20 text-orange-400',
                      pkl: 'bg-purple-500/20 text-purple-400',
                      h5: 'bg-yellow-500/20 text-yellow-400',
                      pb: 'bg-yellow-500/20 text-yellow-400',
                      onnx: 'bg-blue-500/20 text-blue-400',
                      safetensors: 'bg-green-500/20 text-green-400',
                      csv: 'bg-teal-500/20 text-teal-400',
                      json: 'bg-indigo-500/20 text-indigo-400',
                      png: 'bg-pink-500/20 text-pink-400',
                      jpg: 'bg-pink-500/20 text-pink-400',
                      jpeg: 'bg-pink-500/20 text-pink-400',
                      txt: 'bg-gray-500/20 text-gray-400',
                      yaml: 'bg-cyan-500/20 text-cyan-400',
                    };
                    const extColor = extColors[ext] || 'bg-gray-700 text-gray-400';
                    const sizeLabel =
                      artifact.size >= 1024 * 1024
                        ? `${(artifact.size / (1024 * 1024)).toFixed(1)} MB`
                        : artifact.size >= 1024
                        ? `${(artifact.size / 1024).toFixed(1)} KB`
                        : `${artifact.size} B`;
                    const isDownloading = downloadingArtifact === artifact.path;

                    return (
                      <div
                        key={artifact.path}
                        className="flex items-center gap-4 px-6 py-4 hover:bg-gray-800 transition-colors"
                      >
                        <DocumentIcon className="w-5 h-5 text-gray-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-mono text-white truncate">{artifact.path}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {artifact.last_modified
                              ? new Date(artifact.last_modified).toLocaleString()
                              : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {ext && (
                            <span className={`px-2 py-0.5 text-xs font-medium rounded uppercase ${extColor}`}>
                              {ext}
                            </span>
                          )}
                          <span className="text-xs text-gray-400 tabular-nums w-16 text-right">
                            {sizeLabel}
                          </span>
                          <button
                            onClick={() => handleDownloadArtifact(artifact.path)}
                            disabled={isDownloading}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors disabled:opacity-50"
                            title="Download"
                          >
                            {isDownloading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500" />
                            ) : (
                              <ArrowDownTrayIcon className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="px-6 py-3 border-t border-gray-700 text-xs text-gray-500">
                  Total:{' '}
                  {artifacts.reduce((s, a) => s + a.size, 0) >= 1024 * 1024
                    ? `${(artifacts.reduce((s, a) => s + a.size, 0) / (1024 * 1024)).toFixed(1)} MB`
                    : `${(artifacts.reduce((s, a) => s + a.size, 0) / 1024).toFixed(1)} KB`}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notes Tab */}
        {activeTab === 'notes' && (
          <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Run Notes</h3>
            <p className="text-sm text-gray-400 mb-4">
              Capture observations, hypotheses, or any free-form notes about this run.
            </p>
            <textarea
              className="w-full h-64 bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y"
              placeholder="Add notes about this run - what worked, what to try next, observations..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <div className="flex justify-end mt-3">
              <button
                onClick={handleSaveNotes}
                disabled={notesSaving}
                className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {notesSaving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : null}
                Save Notes
              </button>
            </div>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Metric Alerts</h3>
              <button
                onClick={() => setShowCreateAlert(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                New Alert
              </button>
            </div>

            {/* Create Alert Form */}
            {showCreateAlert && (
              <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
                <h4 className="text-sm font-semibold text-white mb-4">Create Alert</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Metric Key</label>
                    <input
                      type="text"
                      className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g. loss"
                      value={newAlert.metric_key}
                      onChange={(e) => setNewAlert((a) => ({ ...a, metric_key: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Condition</label>
                    <select
                      className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={newAlert.condition}
                      onChange={(e) => setNewAlert((a) => ({ ...a, condition: e.target.value }))}
                    >
                      <option value="lt">Less than (&lt;)</option>
                      <option value="lte">Less than or equal (&le;)</option>
                      <option value="gt">Greater than (&gt;)</option>
                      <option value="gte">Greater than or equal (&ge;)</option>
                      <option value="eq">Equal to (=)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Threshold</label>
                    <input
                      type="number"
                      step="any"
                      className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="0.01"
                      value={newAlert.threshold}
                      onChange={(e) => setNewAlert((a) => ({ ...a, threshold: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Notify Email (optional)</label>
                    <input
                      type="email"
                      className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="you@company.com"
                      value={newAlert.notify_email}
                      onChange={(e) => setNewAlert((a) => ({ ...a, notify_email: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-xs text-gray-400 mb-1">Slack Webhook URL (optional)</label>
                  <input
                    type="url"
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="https://hooks.slack.com/services/..."
                    value={newAlert.notify_slack}
                    onChange={(e) => setNewAlert((a) => ({ ...a, notify_slack: e.target.value }))}
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleCreateAlert}
                    disabled={creatingAlert || !newAlert.metric_key || newAlert.threshold === ''}
                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                  >
                    {creatingAlert ? 'Creating...' : 'Create Alert'}
                  </button>
                  <button
                    onClick={() => setShowCreateAlert(false)}
                    className="px-4 py-2 text-gray-400 hover:text-white text-sm font-medium rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Alert List */}
            {alertsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
              </div>
            ) : alerts.length === 0 && !showCreateAlert ? (
              <div className="bg-gray-900 rounded-xl border border-gray-700 p-12 text-center">
                <BellAlertIcon className="mx-auto h-12 w-12 text-gray-600 mb-3" />
                <h3 className="text-sm font-medium text-white mb-1">No alerts configured</h3>
                <p className="text-sm text-gray-400">
                  Create alerts to get notified via email or Slack when a metric crosses a threshold.
                </p>
              </div>
            ) : (
              <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden">
                <div className="divide-y divide-gray-700">
                  {alerts.map((alert) => (
                    <div key={alert.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-800">
                      <div className="flex items-center gap-4">
                        <span className={`w-2 h-2 rounded-full ${alert.status === 'triggered' ? 'bg-orange-400' : 'bg-green-400'}`} />
                        <div>
                          <p className="text-sm font-medium text-white">
                            {alert.metric_key} {alert.condition} {alert.threshold}
                          </p>
                          <p className="text-xs text-gray-400">
                            {alert.notify_email && `Email: ${alert.notify_email}`}
                            {alert.notify_email && alert.notify_slack && ' · '}
                            {alert.notify_slack && 'Slack configured'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${alert.status === 'triggered' ? 'bg-orange-500/20 text-orange-400' : 'bg-green-500/20 text-green-400'}`}>
                          {alert.status}
                        </span>
                        <button
                          onClick={() => handleDeleteAlert(alert.id)}
                          className="p-1.5 text-gray-500 hover:text-red-400 rounded transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Datasets Tab */}
        {activeTab === 'datasets' && (
          <div>
            {datasetsLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              </div>
            ) : datasets.length === 0 ? (
              <div className="bg-gray-900 rounded-xl border border-gray-700 p-12 text-center">
                <CircleStackIcon className="mx-auto h-12 w-12 text-gray-600 mb-3" />
                <h3 className="text-sm font-medium text-white mb-1">No datasets logged</h3>
                <p className="text-sm text-gray-400">
                  Call <code className="font-mono bg-gray-800 px-1 rounded">run.log_dataset()</code> in your training code to track dataset provenance.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {datasets.map((ds) => (
                  <div key={ds.id} className="bg-gray-900 rounded-xl border border-gray-700 p-5">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-base font-semibold text-white">{ds.name}</h4>
                        {ds.source && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs" title={ds.source}>
                            {ds.source}
                          </p>
                        )}
                      </div>
                      {ds.source_type && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-500/20 text-blue-400 ml-2 shrink-0">
                          {ds.source_type}
                        </span>
                      )}
                    </div>

                    {/* Profile pills */}
                    {(ds.profile?.num_rows !== undefined || ds.profile?.num_features !== undefined) && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {ds.profile.num_rows !== undefined && (
                          <span className="px-2 py-0.5 text-xs bg-gray-800 text-gray-300 rounded">
                            {ds.profile.num_rows.toLocaleString()} rows
                          </span>
                        )}
                        {ds.profile.num_features !== undefined && (
                          <span className="px-2 py-0.5 text-xs bg-gray-800 text-gray-300 rounded">
                            {ds.profile.num_features} features
                          </span>
                        )}
                      </div>
                    )}

                    {/* Schema accordion */}
                    {ds.schema && Object.keys(ds.schema).length > 0 && (
                      <div className="mb-3">
                        <button
                          onClick={() => setExpandedSchema(expandedSchema === ds.id ? null : ds.id)}
                          className="text-xs text-gray-400 hover:text-gray-200 flex items-center gap-1"
                        >
                          <span>{expandedSchema === ds.id ? '▾' : '▸'}</span>
                          Schema ({Object.keys(ds.schema).length} columns)
                        </button>
                        {expandedSchema === ds.id && (
                          <div className="mt-2 max-h-40 overflow-y-auto">
                            <table className="w-full text-xs">
                              <tbody>
                                {Object.entries(ds.schema).map(([col, dtype]) => (
                                  <tr key={col} className="border-b border-gray-800">
                                    <td className="py-1 pr-3 font-mono text-gray-300">{col}</td>
                                    <td className="py-1 text-gray-500">{dtype}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Digest */}
                    {ds.digest && (
                      <div className="text-xs text-gray-500 font-mono truncate" title={ds.digest}>
                        sha256: {ds.digest.slice(0, 16)}…
                      </div>
                    )}

                    {/* Created date */}
                    <div className="text-xs text-gray-600 mt-2">
                      {ds.created_at ? new Date(ds.created_at).toLocaleString() : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RunDetail;
